import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GoogleMapView } from '@/features/map/components/GoogleMapView';
import { ZoomButtons } from '@/features/map/components/ZoomButtons';
import { LocationPermissionPrompt } from '@/features/search/components';
import { QuickFilterChips } from '@/features/filters/components/QuickFilterChips';
import { NearbyList } from '@/features/charge-points/components/nearby/NearbyList';
import { MapSearchBar } from '@/features/search/components';
import { cn } from '@/utils/cn';
import { FilterPanel } from '@/features/filters/components/FilterPanel';
import { ChargePointInfoPanel } from '@/features/charge-points/components/info-panel/ChargePointInfoPanel';
import { LanguageSwitcher } from '@/features/locale/components/LanguageSwitcher';
import { ThemeToggle } from '@/features/theme/components/ThemeToggle';
import { ShareButton } from '@/features/share/components/ShareButton';
import { Logo } from '../common/Logo';
import { useFilterStore } from '@/features/filters/store/filter-store';
import {
  useChargePointsStore,
  type ChargePointsState,
} from '@/features/charge-points/store/charge-points-store';
import { useGoogleMapsStore } from '@/features/map/store/google-maps-store';

/**
 * Desktop layout (≥800px)
 */
export function DesktopLayout() {
  const [searchParams] = useSearchParams();
  const isFilterPanelOpen = useFilterStore((s) => s.isFilterPanelOpen);
  const controlsEnabled = useFilterStore((s) => s.controlsEnabled);
  const showShareButton = useFilterStore((s) => s.showShareButton);
  const isDetailsPanelOpen = useChargePointsStore(
    (s: ChargePointsState) => s.isDetailsPanelOpen
  );
  const closeDetails = useChargePointsStore(
    (s: ChargePointsState) => s.closeDetails
  );
  const setShouldInterceptPointer = useGoogleMapsStore(
    (s) => s.setShouldInterceptPointer
  );

  // Get operator from URL (defaults to 'monta')
  const operator = searchParams.get('operator') || 'monta';
  const showOperator = operator.toLowerCase() !== 'monta';

  // Check if map is locked
  const isLocked = searchParams.get('locked') === 'true';

  // The left filter sidebar (statuses, charge speed, price, operators, best
  // price, etc.) is entirely charge-point-specific and doesn't apply to the
  // sessions map, so hide it in charge-sessions mode. Read `mapType` the same
  // way GoogleMapView does. STOPGAP — per-map-type filter/config mapping is
  // tracked in BIZOPS-1600.
  const isChargeSessionsMode = searchParams.get('mapType') === 'chargeSessions';
  const showFilterUi =
    !isChargeSessionsMode && (controlsEnabled || isFilterPanelOpen);

  // Ensure map is always interactive on desktop
  useEffect(() => {
    setShouldInterceptPointer(false);
  }, [setShouldInterceptPointer]);

  // Monta logo (links to monta.com in embeds) + operator sub-logo
  const brand = (
    <>
      {controlsEnabled ? (
        <Logo />
      ) : (
        <a href="https://monta.com" target="_blank" rel="noopener noreferrer">
          <Logo />
        </a>
      )}
      {showOperator && (
        <img
          src="/monta-technology.png"
          alt="Monta Technology"
          className="mt-2 h-4"
        />
      )}
    </>
  );

  return (
    <div className="relative h-screen w-screen overflow-hidden">
      {/* Full screen map */}
      <GoogleMapView />

      {/* Left: glass sidebar with brand, search, quick filters and either the
          nearby list or the full filter panel. Charge-point-specific (hidden
          in charge-sessions mode) and shown if controls are enabled OR
          filter_ui=1 is in the URL. The filter panel stays mounted while
          closed so its URL operator resolution still runs. */}
      {showFilterUi ? (
        <aside className="absolute left-4 top-4 bottom-4 z-10 flex w-[384px] flex-col rounded-[28px] bg-surface/85 text-ink shadow-monta-lg ring-1 ring-ink/10 backdrop-blur-xl backdrop-saturate-150 dark:bg-surface/75">
          <header className="flex shrink-0 flex-col gap-3 p-4 pb-3">
            <div className="px-1 pt-1">{brand}</div>
            {/* Sunken search inside the glass sidebar */}
            <div className="[&_input]:bg-ink/5 [&_input]:shadow-none [&_input]:ring-0 [&_input]:backdrop-blur-none">
              <MapSearchBar />
            </div>
            {controlsEnabled && <QuickFilterChips wrap />}
          </header>
          <div className="flex min-h-0 flex-1 flex-col border-t border-ink/10">
            <div
              className={cn(
                'flex min-h-0 flex-1 flex-col',
                !isFilterPanelOpen && 'hidden'
              )}
            >
              <FilterPanel />
            </div>
            {controlsEnabled && !isFilterPanelOpen && <NearbyList />}
          </div>
        </aside>
      ) : (
        <div className="absolute left-4 top-4 z-10">{brand}</div>
      )}

      {/* Share button - bottom-left, right of the sidebar (its popover opens
          upwards to the right), aligned with the bottom-right zoom buttons */}
      {controlsEnabled && showShareButton && (
        <div
          className={cn(
            'absolute bottom-12 z-20',
            showFilterUi ? 'left-[416px]' : 'left-4'
          )}
        >
          <ShareButton />
        </div>
      )}

      {/* Location permission prompt */}
      <LocationPermissionPrompt />

      {/* Right side panels */}
      <div className="absolute right-0 top-0 h-full pointer-events-none z-10">
        {/* Top controls - language switcher hidden when controls disabled */}
        {controlsEnabled && (
          <div className="absolute right-4 top-4 flex items-center gap-2 pointer-events-auto">
            <ThemeToggle />
            <LanguageSwitcher />
          </div>
        )}

        {/* Details panel */}
        {isDetailsPanelOpen && (
          <div className="absolute right-4 top-20 bottom-4 w-[420px] pointer-events-auto">
            <div className="h-full bg-surface/90 text-ink backdrop-blur-xl backdrop-saturate-150 rounded-[28px] shadow-monta-lg ring-1 ring-ink/10 overflow-hidden dark:bg-surface/80">
              <ChargePointInfoPanel onClose={closeDetails} />
            </div>
          </div>
        )}
      </div>

      {/* Map controls - bottom right corner (hidden when controls disabled or locked) */}
      {controlsEnabled && !isLocked && (
        <div
          className={cn(
            'absolute bottom-12 pointer-events-none z-10',
            // Sit left of the open details panel (420px + 16px gap + 16px edge)
            isDetailsPanelOpen ? 'right-[452px]' : 'right-4'
          )}
        >
          <div className="pointer-events-auto flex flex-col gap-3">
            <ZoomButtons />
          </div>
        </div>
      )}
    </div>
  );
}
